/**
 * Utility for exporting reports to real Excel (.xlsx / .csv) and styled printable PDF documents
 */

export interface ExportReportOptions {
  title: string;
  subtitle?: string;
  filterInfo?: string;
  columns: { header: string; key: string; align?: 'left' | 'center' | 'right'; isCurrency?: boolean }[];
  data: any[];
  summaryRows?: { label: string; value: string | number; isCurrency?: boolean }[];
  schoolName?: string;
  printedBy?: string;
}

/**
 * Clean & format cell value for CSV
 */
const escapeCsv = (val: any): string => {
  if (val === null || val === undefined) return '""';
  const str = String(val).replace(/"/g, '""');
  return `"${str}"`;
};

/**
 * Export data to real CSV (which Excel opens natively with UTF-8 BOM)
 */
export const exportToExcel = (options: ExportReportOptions) => {
  const { title, subtitle, filterInfo, columns, data, summaryRows, schoolName = 'SDMT PONOROGO' } = options;

  const csvRows: string[] = [];

  // UTF-8 BOM so Indonesian chars and Excel open cleanly
  const BOM = '\uFEFF';

  // Header Title metadata
  csvRows.push(escapeCsv(schoolName));
  csvRows.push(escapeCsv(title.toUpperCase()));
  if (subtitle) csvRows.push(escapeCsv(subtitle));
  if (filterInfo) csvRows.push(escapeCsv(`Filter: ${filterInfo}`));
  csvRows.push(escapeCsv(`Tanggal Cetak: ${new Date().toLocaleString('id-ID')}`));
  csvRows.push('""'); // Empty line

  // Column Headers
  const headerRow = columns.map((col) => escapeCsv(col.header)).join(';');
  csvRows.push(headerRow);

  // Data Rows
  data.forEach((row, index) => {
    const rowValues = columns.map((col) => {
      let val = row[col.key];
      if (col.key === '_index') {
        val = index + 1;
      }
      return escapeCsv(val);
    });
    csvRows.push(rowValues.join(';'));
  });

  // Summary Rows
  if (summaryRows && summaryRows.length > 0) {
    csvRows.push('""');
    summaryRows.forEach((s) => {
      const summaryLine = `${escapeCsv(s.label)};${escapeCsv(
        typeof s.value === 'number' ? s.value.toLocaleString('id-ID') : s.value
      )}`;
      csvRows.push(summaryLine);
    });
  }

  const csvContent = BOM + csvRows.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  const filename = `${title.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export report to formatted Printable PDF via print preview window
 */
export const exportToPdf = (options: ExportReportOptions) => {
  const {
    title,
    subtitle = 'Laporan Keuangan & Administrasi Siswa',
    filterInfo,
    columns,
    data,
    summaryRows,
    schoolName = 'SD MUHAMMADIYAH TERPADU (SDMT) PONOROGO',
    printedBy = 'Bendahara / Petugas TU Keuangan',
  } = options;

  let printWindow: Window | null = null;
  try {
    printWindow = window.open('', '_blank');
  } catch (e) {
    printWindow = null;
  }

  const dateStr = new Date().toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const html = `
    <!DOCTYPE html>
    <html lang="id">
    <head>
      <meta charset="utf-8" />
      <title>${title} - ${schoolName}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 1.5cm 1.2cm;
        }
        body {
          font-family: Arial, Helvetica, sans-serif;
          color: #1e293b;
          margin: 0;
          padding: 10px;
          font-size: 11px;
          line-height: 1.4;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #0f172a;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }
        .header h1 {
          margin: 0;
          font-size: 16px;
          font-weight: 800;
          color: #0369a1;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .header h2 {
          margin: 4px 0 0 0;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          text-transform: uppercase;
        }
        .header p {
          margin: 2px 0 0 0;
          font-size: 10px;
          color: #64748b;
        }
        .meta-box {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          font-size: 10px;
          background: #f8fafc;
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 16px;
        }
        th, td {
          border: 1px solid #cbd5e1;
          padding: 5px 6px;
          font-size: 10px;
        }
        th {
          background-color: #f1f5f9;
          font-weight: 700;
          color: #0f172a;
          text-transform: uppercase;
        }
        .text-left { text-align: left; }
        .text-center { text-align: center; }
        .text-right { text-align: right; font-family: monospace; font-size: 10.5px; }
        .font-mono { font-family: monospace; }
        .font-bold { font-weight: bold; }
        .summary-box {
          width: 45%;
          margin-left: auto;
          margin-bottom: 24px;
          border: 1px solid #cbd5e1;
        }
        .summary-box tr td {
          padding: 4px 8px;
        }
        .signature-table {
          width: 100%;
          border: none;
          margin-top: 25px;
          page-break-inside: avoid;
        }
        .signature-table td {
          border: none;
          text-align: center;
          width: 50%;
          font-size: 11px;
        }
        .sign-space {
          height: 60px;
        }
        @media print {
          .no-print { display: none; }
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${schoolName}</h1>
        <h2>${title}</h2>
        <p>Alamat: Jl. Sukarno Hatta, Ponorogo, Jawa Timur | Telp: (0352) 481872</p>
      </div>

      <div class="meta-box">
        <div>
          ${filterInfo ? `<strong>Kriteria:</strong> ${filterInfo}` : ''}
          ${subtitle ? `<div><strong>Deskripsi:</strong> ${subtitle}</div>` : ''}
        </div>
        <div style="text-align: right;">
          <strong>Tanggal Dicetak:</strong> ${dateStr}<br/>
          <strong>Waktu:</strong> ${new Date().toLocaleTimeString('id-ID')}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th class="text-center" style="width: 25px;">NO</th>
            ${columns
              .map(
                (c) =>
                  `<th class="text-${c.align || 'left'}">${c.header}</th>`
              )
              .join('')}
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (row, idx) => `
            <tr>
              <td class="text-center">${idx + 1}</td>
              ${columns
                .map((col) => {
                  let val = row[col.key];
                  if (col.isCurrency && typeof val === 'number') {
                    val = 'Rp ' + val.toLocaleString('id-ID');
                  }
                  return `<td class="text-${col.align || 'left'} ${
                    col.align === 'right' ? 'font-mono' : ''
                  }">${val !== undefined && val !== null ? val : '-'}</td>`;
                })
                .join('')}
            </tr>
          `
            )
            .join('')}
        </tbody>
      </table>

      ${
        summaryRows && summaryRows.length > 0
          ? `
        <table class="summary-box">
          <tbody>
            ${summaryRows
              .map(
                (s) => `
              <tr>
                <td class="text-left"><strong>${s.label}</strong></td>
                <td class="text-right font-bold">${
                  typeof s.value === 'number'
                    ? 'Rp ' + s.value.toLocaleString('id-ID')
                    : s.value
                }</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      `
          : ''
      }

      <table class="signature-table">
        <tr>
          <td>
            Mengetahui,<br/>
            <strong>Kepala Sekolah SDMT Ponorogo</strong>
            <div class="sign-space"></div>
            <strong>( .................................................. )</strong>
          </td>
          <td>
            Ponorogo, ${dateStr}<br/>
            <strong>Petugas Kasir / TU Keuangan</strong>
            <div class="sign-space"></div>
            <strong>( ${printedBy} )</strong>
          </td>
        </tr>
      </table>

      <script>
        window.onload = function() {
          window.print();
        };
      </script>
    </body>
    </html>
  `;

  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  } else {
    // If pop-up blocked by browser/iframe sandbox, use invisible iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          console.error(e);
        }
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 400);
    }
  }
};
